import { supabase } from "@/integrations/supabase/client";

/**
 * Hard-remove the caller's `breakfast_attendees` row for `eventId`.
 *
 * Primary path is the `remove_event_from_my_calendar` RPC (shipped in
 * `20260424120000_outlook_style_cancellations.sql`, archives the
 * matching bell notifs in the same transaction). If that RPC doesn't
 * exist on the target environment yet — which is the most common
 * failure mode in production — we fall back to a direct RLS-guarded
 * DELETE on `breakfast_attendees` so the UI still works.
 *
 * Used by:
 *   • `useRemoveEventFromMyCalendar` — drawer "Remove" button
 *   • `useEventCancellationActions.removeFromCalendar` — notif bell
 */
export async function removeMyAttendeeRow(eventId: string): Promise<void> {
  const { error: rpcErr } = await supabase.rpc(
    "remove_event_from_my_calendar",
    { p_event_id: eventId },
  );

  if (!rpcErr) return;

  const msg = rpcErr.message?.toLowerCase?.() || "";
  const looksLikeMissingRpc =
    msg.includes("could not find the function") ||
    msg.includes("does not exist") ||
    msg.includes("not found");

  if (!looksLikeMissingRpc) {
    throw new Error(rpcErr.message);
  }

  // Fallback path. Log loudly so the ops team notices the missing
  // migration even though end-users get a working UI.
  console.warn(
    "[removeMyAttendeeRow] remove_event_from_my_calendar RPC missing — " +
      "falling back to direct DELETE. Apply migration " +
      "`20260424120000_outlook_style_cancellations.sql` to restore the " +
      "authoritative path.",
  );

  const { data: sessionRes } = await supabase.auth.getSession();
  const meId = sessionRes.session?.user?.id;
  if (!meId) throw new Error("Not authenticated");

  const { error: deleteErr, count } = await supabase
    .from("breakfast_attendees")
    .delete({ count: "exact" })
    .eq("event_id", eventId)
    .eq("user_id", meId);

  if (deleteErr) throw new Error(deleteErr.message);
  if ((count ?? 0) === 0) {
    throw new Error(
      "Could not remove the event. Either no attendee row exists, or " +
        "Row-Level-Security is blocking the delete. Apply the latest " +
        "migration to fix this.",
    );
  }
}
